import { useRouter } from 'next/router'
import { Fragment, createContext, useContext, useEffect, useState } from 'react'

import { faSirenOn } from '@fortawesome/pro-light-svg-icons'
import { faCreditCardAlt } from '@fortawesome/pro-regular-svg-icons'
import { AxiosRequestConfig } from 'axios'

import { Invoice, RetrySubscriptionRequest } from '@infura/packages.api'
import { CreditCard, Subscription } from '@infura/packages.chargify'
import { METAMASK_SUPPORT_URL } from '@infura/packages.common/constants'
import { Button } from '@infura/packages.ui/Button'
import { Heading } from '@infura/packages.ui/Heading'
import { Modal } from '@infura/packages.ui/Modal'
import { SemanticIcon } from '@infura/packages.ui/SemanticIcon'
import { Tag } from '@infura/packages.ui/Tag'
import { Text } from '@infura/packages.ui/Text'

import { CardInfo } from 'components/billing/CardInfo'
import ChangePaymentModal from 'components/billing/ChangePaymentModal'

import { useApiClient } from 'hooks/api/useApiClient'
import useCard from 'hooks/useCard'
import useOutstandingInvoices from 'hooks/useOutstandingInvoices'
import useSubscription from 'hooks/useSubscription'

type ModalState = 'init' | 'invoicesOpen' | 'cardErrorOpen' | 'paymentFormOpen' | 'success' | 'dismissed'

interface ModalStateContextType {
  modalState: ModalState
  setModalState: (state: ModalState) => void
}

const ModalStateContext = createContext<ModalStateContextType | undefined>(undefined)

export const useModalState = () => {
  const context = useContext(ModalStateContext)
  if (context === undefined) {
    throw new Error('useModalState must be used within a ModalStateProvider')
  }
  return context
}

export const ModalStateProvider = ({ children }: { children: React.ReactNode }) => {
  const [modalState, setModalState] = useState<ModalState>('init')

  return <ModalStateContext.Provider value={{ modalState, setModalState }}>{children}</ModalStateContext.Provider>
}

interface BillingInfoUpdatedConfirmationModalProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}

const BillingInfoUpdatedConfirmationModal = ({ isOpen, setIsOpen }: BillingInfoUpdatedConfirmationModalProps) => (
  <Modal open={isOpen} setIsOpen={setIsOpen}>
    <div className='text-center'>
      <SemanticIcon className='mx-auto mb-4' icon={faCreditCardAlt} theme='success' />
      <Heading className='mb-4 text-center' theme='section'>
        Transaction processing
      </Heading>
      <Text className='mb-6 text-center'>
        Your transaction is being processed. Please allow a few minutes for the transaction to complete. If it fails to
        process, this window will display again.
      </Text>
      <div className='text-center'>
        <Button className='mt-4 w-full sm:w-auto' onClick={() => setIsOpen(false)}>
          Continue Building
        </Button>
      </div>
    </div>
  </Modal>
)

interface CardErrorModalProps {
  chargifyCard: CreditCard
  isOpen: boolean
}

const CardErrorModal = ({ chargifyCard, isOpen }: CardErrorModalProps) => {
  const { setModalState } = useModalState()
  return (
    <Modal open={isOpen} setIsOpen={() => setModalState('dismissed')}>
      <SemanticIcon className='mx-auto mb-4' icon={faCreditCardAlt} theme='warning' />
      <Heading className='mb-4 text-center' theme='section'>
        Invalid card on file
      </Heading>
      <Text className='mb-6 text-center'>
        There was an error while trying to pay with your primary payment method. Please update your card information.
      </Text>
      <CardInfo
        creditCard={chargifyCard}
        loading={false}
        togglePaymentForm={() => {
          setModalState('paymentFormOpen')
        }}
      />
    </Modal>
  )
}

interface OutstandingBalanceModalProps {
  isOpen: boolean
  openInvoices: Array<Invoice>
  retrying: boolean
  retrySubscription: (
    retrySubscriptionRequest: RetrySubscriptionRequest,
    options?: AxiosRequestConfig<any>
  ) => Promise<any>
  subscription: Subscription
}

const OutstandingBalanceModal = ({
  isOpen,
  openInvoices,
  retrying,
  retrySubscription,
  subscription
}: OutstandingBalanceModalProps) => {
  const { setModalState } = useModalState()
  return (
    <Modal open={isOpen} setIsOpen={() => setModalState('dismissed')}>
      <SemanticIcon className='mx-auto mb-4' icon={faSirenOn} theme='warning' />
      <Heading className='mb-4 text-center' theme='section'>
        Outstanding Balance
      </Heading>
      <Text className='mb-4 text-center'>
        There is an outstanding balance on your account that needs to be paid before you can proceed with upgrading your
        plan. You can view and pay this balance below.
      </Text>
      <div className='rounded-lg border-2'>
        {openInvoices.map((invoice, index) => (
          <div
            className={`first:rounded-t-lg last:rounded-b-lg ${
              index !== 0 ? 'border-t-2' : ''
            } xs:grid-cols-4 bg-background-alternative grid grid-cols-2 items-center gap-4 gap-y-0 p-4 sm:justify-between`}
            key={invoice.uid}
          >
            <Text className='xs:text-center text-sm sm:text-base'>
              {/* @ts-expect-error enabled to support strictNullChecks - Please fix this if you can! */}
              {new Date(invoice.issue_date).toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}
            </Text>
            <Text className='xs:text-center text-sm sm:text-base'>{invoice.product_name.replace('Infura+ ', '')}</Text>
            <Text className='xs:text-center items-center text-sm sm:text-base'>
              US
              {new Intl.NumberFormat('en-US', { currency: 'USD', style: 'currency' }).format(
                parseFloat(invoice.due_amount)
              )}
            </Text>
            <Text className='xs:text-center xs:mx-auto text-error-default text-sm sm:text-base'>
              {invoice.product_name !== 'Infura+ Custom' ? (
                <a className='text-error-default' href={invoice.public_url} rel='noopener noreferrer' target='_blank'>
                  <Tag theme='error'>Unpaid</Tag>
                </a>
              ) : (
                <Tag theme='error'>Unpaid</Tag>
              )}
            </Text>
          </div>
        ))}
      </div>
      <div className='flex items-center justify-center gap-4 space-x-2 p-4'>
        {subscription?.product?.name !== 'Infura+ Custom' ? (
          <Button
            className='min-w-40'
            loading={retrying}
            onClick={() => {
              retrySubscription({ subscriptionId: subscription?.id })
                .then(response => {
                  if (response.data?.subscription) {
                    setModalState('success')
                  }
                })
                .catch(() => {
                  setModalState('cardErrorOpen')
                })
            }}
            preserveTextWhileLoading={true}
          >
            Pay Balance
          </Button>
        ) : null}
        <Button href={`${METAMASK_SUPPORT_URL}/develop`} hrefTarget='_blank' theme='secondary'>
          Contact Us
        </Button>
      </div>
    </Modal>
  )
}

export const OutstandingInvoicesModal = () => {
  const { modalState, setModalState } = useModalState()
  const { loading: invoicesLoading, openInvoices: invoices } = useOutstandingInvoices()
  const { loading: subscriptionLoading, subscription } = useSubscription()
  const [openInvoices, setOpenInvoices] = useState<Array<Invoice>>([])
  const { card: chargifyCard } = useCard()
  const {
    call: retrySubscription,
    data: retryResponse,
    error: retryError,
    loading: retrying
  } = useApiClient('retrySubscription')

  const { pathname } = useRouter()

  useEffect(() => {
    // Do not pop it on the /support/ticket page since we are linking there from the modal.
    if (pathname === '/support/ticket') {
      return
    }
    if (subscription?.state === 'past_due' && invoices) {
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
      const isAnyInvoicePastDue = invoices.some(invoice => {
        const dueDate = new Date(invoice.due_date)
        return invoice.status === 'open' && dueDate < sevenDaysAgo
      })
      setOpenInvoices(invoices)
      if (isAnyInvoicePastDue && modalState === 'init') {
        setModalState('invoicesOpen')
      }
    }
  }, [subscription, invoices, invoicesLoading, subscriptionLoading, setModalState, modalState, pathname])

  // Modal Flow Summary:
  // - "Pay Balance" click: Success -> Show success modal, Failure -> Show card error modal.
  // - Updating payment method retries payment and shows transaction modal.

  return (
    <Fragment>
      <BillingInfoUpdatedConfirmationModal
        isOpen={modalState === 'success'}
        setIsOpen={() => setModalState('dismissed')}
      />
      <ChangePaymentModal
        closeOnClickOutside={false}
        hiddenCloseBtn={true}
        onToggle={() => {
          setModalState('success')
        }}
        open={modalState === 'paymentFormOpen'}
        // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
        title={null}
      />
      {/* @ts-expect-error enabled to support strictNullChecks - Please fix this if you can! */}
      <CardErrorModal chargifyCard={chargifyCard} isOpen={modalState === 'cardErrorOpen'} />
      <OutstandingBalanceModal
        isOpen={!retryError && !retryResponse ? modalState === 'invoicesOpen' : false}
        openInvoices={openInvoices}
        retrying={retrying}
        retrySubscription={retrySubscription}
        subscription={subscription}
      />
    </Fragment>
  )
}
